import React, { useCallback, useEffect } from "react";
import { isVideoUrl as isDirectVideoFile } from "@zapier/rich-text-editor";
import styles from "./VideoPlayer.module.css";

export type VideoProvider = "youtube" | "youtu.be" | "vimeo" | "loom" | null;

function getProviderFromUrl(url: string): VideoProvider {
  if (!url) return null;

  const urlLower = url.toLowerCase();

  if (urlLower.includes("youtube.com") || urlLower.includes("youtu.be")) {
    return urlLower.includes("youtu.be") ? "youtu.be" : "youtube";
  }
  if (urlLower.includes("vimeo.com")) return "vimeo";
  if (urlLower.includes("loom.com")) return "loom";

  return null;
}

export function isValidVideoProviderUrl(url: string): boolean {
  return getProviderFromUrl(url) !== null;
}

export function isValidVideoUrl(url: string): boolean {
  return isDirectVideoFile(url) || isValidVideoProviderUrl(url);
}

interface VideoPlayerProps {
  url: string;
  shouldAutoplay?: boolean;
  updateNodeSize?: (width: number, height: number) => void;
  isYouTubeShorts?: boolean;
  onIframeLoad?: () => void;
}

export function VideoPlayer({
  url,
  shouldAutoplay = false,
  updateNodeSize,
  isYouTubeShorts = false,
  onIframeLoad,
}: VideoPlayerProps) {
  const isDirectVideo = isDirectVideoFile(url);

  const optimizeVideoSize = useCallback(
    (video: HTMLVideoElement) => {
      if (updateNodeSize && video.videoWidth && video.videoHeight) {
        const aspectRatio = video.videoWidth / video.videoHeight;
        // Calculate dimensions to fit nicely in the embed
        // Handles all direct video files: .mp4, .webm, .mov, etc. (including Zappy, user uploads, screen recordings)
        const width = Math.max(350, Math.min(450, video.videoWidth / 10)); // Min 350px, max 450px width
        const height = width / aspectRatio;

        updateNodeSize(width, height);
      }
    },
    [updateNodeSize],
  );

  // Set portrait dimensions for YouTube Shorts
  // Note: Unlike direct videos, iframes can't access embedded content dimensions due to CORS.
  // We detect by URL pattern and apply standard 9:16 Shorts dimensions.
  useEffect(() => {
    if (updateNodeSize && isYouTubeShorts) {
      // YouTube Shorts are portrait videos with 9:16 aspect ratio
      // Standard Shorts embed dimensions
      const shortsWidth = 315;
      const shortsHeight = 560;
      updateNodeSize(shortsWidth, shortsHeight);
    }
  }, [isYouTubeShorts, updateNodeSize]);

  // Add autoplay parameter to URL for platform embeds if needed
  const videoUrl = React.useMemo(() => {
    if (!shouldAutoplay) return url;

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set("autoplay", "1");

      return urlObj.toString();
    } catch {
      return url;
    }
  }, [url, shouldAutoplay]);

  // If it's neither a direct video file nor a supported platform, return null
  if (!isDirectVideo && !isValidVideoProviderUrl(url)) {
    return null;
  }

  // Handle direct video files (.mp4, .webm, etc.) with <video> tag
  if (isDirectVideo) {
    return (
      <div className={styles.videoWrapper}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          src={url}
          className={styles.video}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) =>
            optimizeVideoSize(e.target as HTMLVideoElement)
          }
        />
      </div>
    );
  }

  // Handle platform embeds (YouTube, Vimeo, Loom) with iframe
  return (
    <div className={styles.videoWrapper}>
      <iframe
        src={videoUrl}
        className={styles.video}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Video player"
        tabIndex={-1}
        onLoad={onIframeLoad}
      />
    </div>
  );
}
